/**
 * StudyLabelControl (AIE-539)
 *
 * Per-study label affordance shown in the card header:
 *   - Unlabeled: a faint, always-visible tag icon that brightens on hover.
 *   - Labeled: an always-visible colored chip (truncated + tooltip) in the label's
 *     category color.
 * Clicking either opens an inline editor: a text field (autofocus, <=50 chars) plus
 * a preset swatch row. Saves on Enter or blur (focus leaving the editor); cancels on
 * Escape; an empty value clears the label.
 *
 * Presentational + interaction only — persistence is delegated to `onSave`
 * (see `useSaveStudyLabel`), so this component is trivially unit-testable.
 */

import { Tag } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/utils/cn";
import {
  DEFAULT_LABEL_COLOR,
  LABEL_COLOR_KEYS,
  LABEL_COLOR_STYLES,
  MAX_LABEL_LENGTH,
  getLabelColorStyle,
  isLabelColorKey,
  type LabelColorKey,
} from "@/utils/labelColors";

export interface StudyLabelControlProps {
  label?: string | null;
  color?: string | null;
  /**
   * Existing labels (lowercased/trimmed) -> color key, used to preselect a color
   * when the user types a label that already exists elsewhere in their library.
   */
  knownLabelColors?: Record<string, string>;
  /**
   * Persist the label. An empty string clears it. `color` is the persist intent:
   * a concrete key sets/recolors the category; `null` means "keep the existing
   * category color" (used when re-applying a known label without touching color).
   */
  onSave: (label: string, color: LabelColorKey | null) => void;
  disabled?: boolean;
  className?: string;
}

export function StudyLabelControl({
  label,
  color,
  knownLabelColors = {},
  onSave,
  disabled,
  className,
}: StudyLabelControlProps) {
  const trimmedLabel = (label ?? "").trim();
  const hasLabel = trimmedLabel.length > 0;
  const currentColorKey: LabelColorKey = isLabelColorKey(color)
    ? color
    : DEFAULT_LABEL_COLOR;

  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(trimmedLabel);
  const [selectedColor, setSelectedColor] =
    useState<LabelColorKey>(currentColorKey);
  const colorTouched = useRef(false);
  const committedRef = useRef(false); // guards against Enter-then-blur double commit
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [editing]);

  const openEditor = () => {
    if (disabled) return;
    setValue(trimmedLabel);
    setSelectedColor(currentColorKey);
    colorTouched.current = false;
    committedRef.current = false;
    setEditing(true);
  };

  const commit = () => {
    if (committedRef.current) return;
    committedRef.current = true;
    setEditing(false);

    const next = value.trim().slice(0, MAX_LABEL_LENGTH);
    const changedLabel = next !== trimmedLabel;
    const changedColor = next.length > 0 && selectedColor !== currentColorKey;
    if (!changedLabel && !changedColor) return; // no-op

    if (next.length === 0) {
      if (hasLabel) onSave("", null); // clear
      return;
    }

    // Only send a concrete color when the user picked one or the label looks new;
    // otherwise send null so the backend keeps the existing category color.
    const known = knownLabelColors[next.toLowerCase()];
    const persistColor: LabelColorKey | null =
      colorTouched.current || !isLabelColorKey(known) ? selectedColor : null;
    onSave(next, persistColor);
  };

  const cancel = () => {
    committedRef.current = true;
    setEditing(false);
  };

  const handleTextChange = (raw: string) => {
    const capped = raw.slice(0, MAX_LABEL_LENGTH);
    setValue(capped);
    if (!colorTouched.current) {
      const known = knownLabelColors[capped.trim().toLowerCase()];
      if (isLabelColorKey(known)) setSelectedColor(known);
    }
  };

  const pickColor = (key: LabelColorKey) => {
    colorTouched.current = true;
    setSelectedColor(key);
    inputRef.current?.focus();
  };

  if (editing) {
    return (
      <div
        className={cn("flex items-center gap-1.5", className)}
        onClick={(e) => e.stopPropagation()}
        onBlur={(e) => {
          // Save when focus leaves the whole editor (click outside / tab away).
          if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
            commit();
          }
        }}
      >
        <Tag className="h-3.5 w-3.5 shrink-0 text-dark-400" aria-hidden />
        <input
          ref={inputRef}
          value={value}
          maxLength={MAX_LABEL_LENGTH}
          placeholder="Add label…"
          aria-label="Study label"
          className="h-6 w-28 min-w-0 rounded border border-dark-600 bg-dark-900 px-1.5 text-xs text-white outline-none focus:border-blue-500"
          onChange={(e) => handleTextChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              commit();
            } else if (e.key === "Escape") {
              e.preventDefault();
              cancel();
            }
          }}
        />
        <div
          className="flex items-center gap-1"
          role="group"
          aria-label="Label color"
        >
          {LABEL_COLOR_KEYS.map((key) => (
            <button
              key={key}
              type="button"
              aria-label={`Color ${key}`}
              aria-pressed={selectedColor === key}
              onClick={() => pickColor(key)}
              className={cn(
                "h-3.5 w-3.5 rounded-full ring-offset-1 ring-offset-dark-900 transition",
                LABEL_COLOR_STYLES[key].swatch,
                selectedColor === key
                  ? "ring-2 ring-white"
                  : "opacity-70 hover:opacity-100",
              )}
            />
          ))}
        </div>
      </div>
    );
  }

  if (hasLabel) {
    const style = getLabelColorStyle(color);
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              aria-label={`Edit label: ${trimmedLabel}`}
              onClick={(e) => {
                e.stopPropagation();
                openEditor();
              }}
              className={cn(
                "inline-flex max-w-[8rem] items-center gap-1 rounded-full border px-1.5 py-0.5 text-[10px] font-medium leading-none transition",
                style.chip,
                className,
              )}
            >
              <Tag className="h-2.5 w-2.5 shrink-0" aria-hidden />
              <span className="truncate">{trimmedLabel}</span>
            </button>
          </TooltipTrigger>
          <TooltipContent>{trimmedLabel}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <button
      type="button"
      aria-label="Add label"
      onClick={(e) => {
        e.stopPropagation();
        openEditor();
      }}
      className={cn(
        "inline-flex h-5 w-5 items-center justify-center rounded text-dark-600 opacity-50 transition hover:text-dark-300 hover:opacity-100",
        className,
      )}
    >
      <Tag className="h-3.5 w-3.5" aria-hidden />
    </button>
  );
}
